// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, IconButton,
  Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
  Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { dateTime } from '../utils/dates';
import { errorMessage } from '../utils/erreurs';
import { saveFile } from '../utils/fichiers';
import { filterItems, locationText } from '../utils/recherche';
import ChampRecherche from '../components/ChampRecherche';

/**
 * Réservations du kit et demandes d'intérêt des particuliers venues de
 * vigeos.fr (demande de Romain du 15/10/2026) : une trace de chaque demande et
 * une idée du nombre de box à fabriquer. Lecture seule ; chaque ligne mène à
 * la fiche prospect, qui reste la seule trace (GET /api/reservations,
 * services/family/reservations_kit.py).
 *
 * Une réservation se fait sans paiement : les montants sont un ordre de
 * grandeur pour la fabrication, pas un chiffre d'affaires.
 */

const FILTER_TABS = [
  { value: '', label: 'Toutes' },
  { value: 'reservation', label: 'Réservations' },
  { value: 'interet', label: 'Intérêts' },
];

const TYPE_LABELS = { reservation: 'Réservation', interet: 'Intérêt' };
const SOURCE_LABELS = {
  accueil: 'Accueil',
  boutique: 'Boutique',
  "liste d'attente": "Liste d'attente",
  contact: 'Contact',
};

function Tile({ title, value, detail }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography color="text.secondary" variant="body2" gutterBottom>{title}</Typography>
        <Typography variant="h4">{value}</Typography>
        {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
      </CardContent>
    </Card>
  );
}

function fullName(row) {
  return [row.first_name, row.last_name].filter(Boolean).join(' ');
}

function euros(n) {
  return `${Number(n || 0).toLocaleString('fr-FR')} €`;
}

// Point-virgule et BOM : le fichier s'ouvre tel quel dans Excel en français.
function csvCell(value) {
  const s = value === null || value === undefined ? '' : String(value);
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportCsv(rows) {
  const header = ['Date', 'Type', 'Source', 'Prénom', 'Nom', 'Adresse électronique',
    'Téléphone', 'Code postal', 'Ville', 'Caméras', 'Prix du kit (€)', 'Statut'];
  const lines = rows.map((r) => [
    dateTime(r.created_at, ''), TYPE_LABELS[r.type] || r.type, SOURCE_LABELS[r.source] || r.source,
    r.first_name, r.last_name, r.email, r.phone, r.postal_code, r.city,
    r.cameras, r.price_eur, r.status,
  ].map(csvCell).join(';'));
  const blob = new Blob([`﻿${[header.join(';'), ...lines].join('\r\n')}\r\n`],
    { type: 'text/csv;charset=utf-8' });
  const day = new Date().toISOString().slice(0, 10);
  saveFile(blob, `reservations-vigeos-${day}.csv`, { revocationDiffereeMs: 1000 });
}

export default function ReservationsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [recherche, setRecherche] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/reservations/');
      setData(res.data);
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'Impossible de charger les réservations'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const requests = useMemo(() => data?.requests || [], [data]);
  const byType = useMemo(
    () => (filterType ? requests.filter((r) => r.type === filterType) : requests),
    [requests, filterType],
  );
  const shown = useMemo(
    () => filterItems(byType, recherche, (r) => [fullName(r), r.email, r.phone,
      locationText(r.postal_code, r.city)]),
    [byType, recherche],
  );
  const totals = data?.totals;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Réservations et intérêts</Typography>
        <Tooltip title="Actualiser">
          <span>
            <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          disabled={!shown.length}
          onClick={() => exportCsv(shown)}
        >
          Export CSV
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Demandes des particuliers sur vigeos.fr : réservations du kit, sans paiement, et
        demandes d’être prévenu. Les montants sont un ordre de grandeur pour la fabrication.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {totals && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Tile title="Kits réservés" value={totals.kits}
              detail={`${totals.reservations} réservation${totals.reservations > 1 ? 's' : ''}`} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Tile title="Caméras réservées" value={totals.cameras}
              detail={`Kits à 1 / 2 / 3 caméras : ${totals.by_cameras['1']} / ${totals.by_cameras['2']} / ${totals.by_cameras['3']}`} />
          </Grid>
          <Grid item xs={6} md={3}>
            <Tile title="Intéressés" value={totals.interests} detail="À prévenir de la sortie" />
          </Grid>
          <Grid item xs={6} md={3}>
            <Tile title="Kits réservés, en valeur" value={euros(totals.amount_eur)}
              detail="Sans paiement, hors abonnement" />
          </Grid>
        </Grid>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <Tabs value={filterType} onChange={(_, v) => setFilterType(v)}>
          {FILTER_TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
        </Tabs>
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          placeholder="Rechercher par nom, adresse, téléphone ou code postal"
          compte={shown.length}
          total={byType.length}
          nomObjet="demande"
        />
      </Box>

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Adresse électronique</TableCell>
                  <TableCell>Localisation</TableCell>
                  <TableCell align="right">Caméras</TableCell>
                  <TableCell align="right">Kit</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((r) => (
                  <TableRow
                    key={r.client_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/clients/${r.client_id}`)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(r.created_at, '—')}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={TYPE_LABELS[r.type] || r.type}
                        color={r.type === 'reservation' ? 'primary' : 'default'}
                        variant={r.type === 'reservation' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{fullName(r) || '—'}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{locationText(r.postal_code, r.city) || '—'}</TableCell>
                    <TableCell align="right">{r.cameras ?? '—'}</TableCell>
                    <TableCell align="right">{r.price_eur ? euros(r.price_eur) : '—'}</TableCell>
                    <TableCell>{SOURCE_LABELS[r.source] || r.source}</TableCell>
                    <TableCell>{r.status}</TableCell>
                  </TableRow>
                ))}
                {!shown.length && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        {requests.length ? 'Aucune demande ne correspond.' : 'Aucune demande pour l’instant.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
